// ✨ Compact & Elegant Login with Red-Yellow Theme + JWT Session Management
import { useState, useEffect } from "react";
import {
  FaEye,
  FaEyeSlash,
  FaEnvelope,
  FaLock,
  FaArrowLeft,
  FaMobile,
  FaKey,
  FaCheckCircle,
  FaShieldAlt,
  FaExclamationTriangle,
} from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  login,
  sendForgotPasswordEmail,
  sendForgotPasswordMobile,
  verifyForgotPasswordOTP,
  resetPassword,
} from "../api/authApi";
import jwtTokenService from "../services/jwtTokenService";
import sessionDeviceManager from "../services/sessionDeviceManager";
import GoogleSignInButton from "./GoogleSignInButton";

const LoginForm = () => {
  const [currentMode, setCurrentMode] = useState("login");
  const [forgotPasswordType, setForgotPasswordType] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpTimer, setOtpTimer] = useState(0);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [deviceWarning, setDeviceWarning] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if already logged in
  useEffect(() => {
    if (jwtTokenService.isAuthenticated()) {
      const from = location.state?.from || "/";
      navigate(from, { replace: true });
    }
  }, [navigate, location]);

  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => setOtpTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePassword = (password) => password.length >= 6;
  const validateMobile = (mobile) => /^[6-9]\d{9}$/.test(mobile);

  const handleLogin = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!validateEmail(email)) newErrors.email = "Enter a valid email";
    if (!validatePassword(password))
      newErrors.password = "Password must be at least 6 characters";
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    setLoading(true);
    setErrors({});
    setDeviceWarning(null);

    try {
      // Step 1: Validate device (check for existing sessions)
      const deviceValidation = await sessionDeviceManager.validateDeviceLogin(
        email,
        password
      );

      // Step 2: Handle device warning if exists
      if (
        deviceValidation.existingSessions &&
        deviceValidation.existingSessions.length > 0
      ) {
        setDeviceWarning({
          sessions: deviceValidation.existingSessions,
          email: email,
          password: password,
        });
        setLoading(false);
        return;
      }

      // Step 3: Proceed with login
      const response = await login({ email, password });

      if (response.data && response.data.token) {
        // Store token using JWT Token Service (7-day expiry)
        const expiresIn = 7 * 24 * 60 * 60; // 7 days in seconds
        jwtTokenService.setToken(response.data.token, expiresIn);
        jwtTokenService.setUser(response.data.user);

        // Step 4: Register device session
        await sessionDeviceManager.registerDeviceSession(
          response.data.user.id,
          email,
          response.data.token
        );

        // Show device info
        const deviceDesc = sessionDeviceManager.getDeviceDescription();
        toast.success(`Login successful! Device: ${deviceDesc}`, {
          autoClose: 4000,
        });

        // Dispatch login event to notify navbar and other components
        window.dispatchEvent(
          new CustomEvent("login", {
            detail: { user: response.data.user },
          })
        );

        // Redirect to intended page or home
        const from = location.state?.from || "/";
        navigate(from, { replace: true });
      }
    } catch (err) {
      const errorMessage = err.response
        ? err.response.data?.message || "Invalid email or password"
        : "Cannot connect to server. Please check your connection.";
      setErrors({ general: errorMessage });
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle force login (terminate other sessions)
  const handleForceLogin = async () => {
    if (!deviceWarning) return;

    setLoading(true);
    try {
      // Login normally
      const response = await login({
        email: deviceWarning.email,
        password: deviceWarning.password,
      });

      if (response.data && response.data.token) {
        // Store token
        const expiresIn = 7 * 24 * 60 * 60; // 7 days
        jwtTokenService.setToken(response.data.token, expiresIn);
        jwtTokenService.setUser(response.data.user);

        // Terminate other sessions
        await sessionDeviceManager.terminateOtherSessions(
          response.data.user.id,
          response.data.token
        );

        // Register new session
        await sessionDeviceManager.registerDeviceSession(
          response.data.user.id,
          deviceWarning.email,
          response.data.token
        );

        toast.success("Login successful! Other sessions have been terminated.");

        // Dispatch login event to notify navbar and other components
        window.dispatchEvent(
          new CustomEvent("login", {
            detail: { user: response.data.user },
          })
        );

        const from = location.state?.from || "/";
        navigate(from, { replace: true });
      }
    } catch (err) {
      toast.error("Failed to force login. Please try again.");
    } finally {
      setLoading(false);
      setDeviceWarning(null);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (forgotPasswordType === "email") {
      if (!validateEmail(email)) newErrors.email = "Enter a valid email";
    } else {
      if (!validateMobile(mobile))
        newErrors.mobile = "Enter a valid 10-digit mobile number";
    }
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    setLoading(true);
    setErrors({});
    try {
      if (forgotPasswordType === "email") {
        await sendForgotPasswordEmail(email);
        toast.success("OTP sent to your email!");
      } else {
        await sendForgotPasswordMobile(mobile);
        toast.success("OTP sent to your mobile!");
      }
      setOtpTimer(300);
      setCurrentMode("otp-verify");
    } catch (err) {
      setErrors({ general: "Failed to send OTP. Please try again." });
      toast.error("Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerification = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      setErrors({ otp: "Enter a valid 6-digit OTP" });
      return;
    }
    setLoading(true);
    setErrors({});
    try {
      const response = await verifyForgotPasswordOTP(
        forgotPasswordType === "email" ? email : mobile,
        otp,
        forgotPasswordType
      );
      // Store the resetToken from response
      if (response.data && response.data.resetToken) {
        setResetToken(response.data.resetToken);
      }
      toast.success("OTP verified successfully!");
      setCurrentMode("reset-password");
    } catch (err) {
      setErrors({ otp: "Invalid OTP. Please try again." });
      toast.error("Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (newPassword.length < 6)
      newErrors.newPassword = "Password must be at least 6 characters";
    if (newPassword !== confirmPassword)
      newErrors.confirmPassword = "Passwords do not match";
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    setLoading(true);
    setErrors({});
    try {
      await resetPassword(
        resetToken,
        newPassword,
        forgotPasswordType === "email" ? email : mobile,
        forgotPasswordType
      );
      toast.success(
        "Password reset successfully! You can now login with your new password."
      );
      // Clear form and go back to login
      setEmail("");
      setMobile("");
      setPassword("");
      setOtp("");
      setNewPassword("");
      setConfirmPassword("");
      setResetToken("");
      setCurrentMode("login");
    } catch (err) {
      setErrors({ general: "Failed to reset password. Please try again." });
      toast.error("Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const renderLoginForm = () => (
    <form onSubmit={handleLogin} className="space-y-3">
      {/* Email */}
      <div>
        <label className="block text-xs font-bold text-[#2E2E2E] mb-1">
          Email
        </label>
        <div className="relative">
          <FaEnvelope className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#EE2E24] text-xs" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full pl-8 pr-2.5 py-2 border-2 border-gray-200 rounded-lg focus:border-[#EE2E24] focus:ring-2 focus:ring-[#FFC400]/20 outline-none transition-all text-sm"
            placeholder="your@email.com"
          />
        </div>
        {errors.email && (
          <p className="mt-0.5 text-[10px] text-[#EE2E24] font-medium">
            {errors.email}
          </p>
        )}
      </div>

      {/* Password */}
      <div>
        <label className="block text-xs font-bold text-[#2E2E2E] mb-1">
          Password
        </label>
        <div className="relative">
          <FaLock className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#EE2E24] text-xs" />
          <input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full pl-8 pr-9 py-2 border-2 border-gray-200 rounded-lg focus:border-[#EE2E24] focus:ring-2 focus:ring-[#FFC400]/20 outline-none transition-all text-sm"
            placeholder="Enter password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#EE2E24] transition-colors"
          >
            {showPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
          </button>
        </div>
        {errors.password && (
          <p className="mt-0.5 text-[10px] text-[#EE2E24] font-medium">
            {errors.password}
          </p>
        )}
      </div>

      {/* Forgot Password Link */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setCurrentMode("forgot")}
          className="text-[11px] text-[#EE2E24] hover:text-[#FFC400] font-bold transition-colors"
        >
          Forgot Password?
        </button>
      </div>

      {/* Login Button */}
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-gradient-to-r from-[#EE2E24] to-[#FFC400] text-white py-2 rounded-lg font-bold text-sm shadow-md shadow-[#EE2E24]/30 hover:shadow-lg hover:shadow-[#FFC400]/40 transition-all hover:scale-[1.02] disabled:opacity-50"
      >
        {loading ? (
          <div className="flex items-center justify-center">
            <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Signing in...
          </div>
        ) : (
          "Sign In"
        )}
      </button>

      {/* Sign Up Link */}
      <div className="text-center pt-2.5 border-t border-gray-200">
        <p className="text-[11px] text-gray-600">
          Don't have an account?{" "}
          <a
            href="/signup"
            className="text-[#EE2E24] font-bold hover:text-[#FFC400] transition-colors"
          >
            Create Account
          </a>
        </p>
      </div>

      {/* Divider */}
      <div className="relative py-2">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300"></div>
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-white px-2 text-gray-500">Or continue with</span>
        </div>
      </div>

      {/* Google Sign In */}
      <GoogleSignInButton />
    </form>
  );

  const renderForgotPasswordForm = () => (
    <form onSubmit={handleForgotPassword} className="space-y-4">
      {/* Back Button */}
      <button
        type="button"
        onClick={() => setCurrentMode("login")}
        className="flex items-center text-[#EE2E24] hover:text-[#FFC400] font-bold text-sm transition-colors mb-2"
      >
        <FaArrowLeft className="mr-1.5 text-xs" /> Back to Login
      </button>

      {/* Email/SMS Tabs */}
      <div className="flex bg-[#F5F5F5] rounded-lg p-0.5 mb-4">
        <button
          type="button"
          onClick={() => setForgotPasswordType("email")}
          className={`flex-1 py-2 rounded-md font-bold text-xs transition-all duration-300 ${
            forgotPasswordType === "email"
              ? "bg-gradient-to-r from-[#EE2E24] to-[#FFC400] text-white shadow-md"
              : "text-[#2E2E2E] hover:text-[#EE2E24]"
          }`}
        >
          <FaEnvelope className="inline mr-1.5 text-xs" />
          Email
        </button>
        <button
          type="button"
          onClick={() => setForgotPasswordType("mobile")}
          className={`flex-1 py-2 rounded-md font-bold text-xs transition-all duration-300 ${
            forgotPasswordType === "mobile"
              ? "bg-gradient-to-r from-[#EE2E24] to-[#FFC400] text-white shadow-md"
              : "text-[#2E2E2E] hover:text-[#EE2E24]"
          }`}
        >
          <FaMobile className="inline mr-1.5 text-xs" />
          SMS
        </button>
      </div>

      {/* Input Fields */}
      {forgotPasswordType === "email" ? (
        <div>
          <label className="block text-xs font-bold text-[#2E2E2E] mb-1.5">
            Email Address
          </label>
          <div className="relative">
            <FaEnvelope className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#EE2E24] text-xs" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full pl-8 pr-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#EE2E24] focus:ring-2 focus:ring-[#FFC400]/20 outline-none transition-all text-sm"
              placeholder="Enter your email"
            />
          </div>
        </div>
      ) : (
        <div>
          <label className="block text-xs font-bold text-[#2E2E2E] mb-1.5">
            Mobile Number
          </label>
          <div className="relative">
            <FaMobile className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#EE2E24] text-xs" />
            <input
              type="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              className="w-full pl-8 pr-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#EE2E24] focus:ring-2 focus:ring-[#FFC400]/20 outline-none transition-all text-sm"
              placeholder="Enter 10-digit mobile"
            />
          </div>
        </div>
      )}

      {/* Send OTP Button */}
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-gradient-to-r from-[#EE2E24] to-[#FFC400] text-white py-2 rounded-lg font-bold text-sm shadow-md shadow-[#EE2E24]/30 hover:shadow-lg hover:shadow-[#FFC400]/40 transition-all hover:scale-[1.02] disabled:opacity-50"
      >
        {loading ? "Sending..." : "Send OTP"}
      </button>
    </form>
  );

  const renderOTPForm = () => (
    <form onSubmit={handleOTPVerification} className="space-y-4">
      {/* Back Button */}
      <button
        type="button"
        onClick={() => setCurrentMode("forgot")}
        className="flex items-center text-[#EE2E24] hover:text-[#FFC400] font-bold text-sm transition-colors mb-2"
      >
        <FaArrowLeft className="mr-1.5 text-xs" /> Back
      </button>

      {/* OTP Input */}
      <div>
        <label className="block text-sm font-bold text-[#2E2E2E] mb-2 text-center">
          Enter 6-Digit OTP
        </label>
        <div className="relative">
          <input
            type="text"
            value={otp}
            onChange={(e) =>
              setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg text-center text-2xl font-bold tracking-[0.4em] focus:border-[#EE2E24] focus:ring-2 focus:ring-[#FFC400]/20 outline-none transition-all"
            placeholder="● ● ● ● ● ●"
            maxLength={6}
            required
          />
        </div>
        {errors.otp && (
          <p className="text-[#EE2E24] text-xs mt-1.5 text-center font-medium">
            {errors.otp}
          </p>
        )}
      </div>

      {/* Timer Display */}
      {otpTimer > 0 && (
        <div className="flex justify-center">
          <div className="bg-[#FFC400]/10 text-[#EE2E24] px-3 py-1.5 rounded-full text-xs font-bold border border-[#FFC400]/30">
            ⏱ Resend OTP in {Math.floor(otpTimer / 60)}:
            {(otpTimer % 60).toString().padStart(2, "0")}
          </div>
        </div>
      )}

      {/* Verify Button */}
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-gradient-to-r from-[#EE2E24] to-[#FFC400] text-white py-3 rounded-lg font-bold text-base shadow-md shadow-[#EE2E24]/30 hover:shadow-lg hover:shadow-[#FFC400]/40 transition-all hover:scale-[1.02] disabled:opacity-50"
      >
        {loading ? (
          <span className="flex items-center justify-center">
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Verifying...
          </span>
        ) : (
          "Verify OTP"
        )}
      </button>

      {/* Resend Button */}
      {otpTimer === 0 && (
        <button
          type="button"
          onClick={() => {
            setOtpTimer(300);
            toast.success("OTP resent!");
          }}
          className="w-full text-[#EE2E24] hover:text-[#FFC400] font-bold text-xs py-1.5 transition-colors"
        >
          Resend OTP
        </button>
      )}
    </form>
  );

  const renderResetPasswordForm = () => (
    <form onSubmit={handlePasswordReset} className="space-y-4">
      {/* New Password Field */}
      <div>
        <label className="block text-xs font-bold text-[#2E2E2E] mb-1.5">
          New Password
        </label>
        <div className="relative">
          <FaLock className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#EE2E24] text-xs" />
          <input
            type={showPassword ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full pl-8 pr-9 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#EE2E24] focus:ring-2 focus:ring-[#FFC400]/20 outline-none transition-all text-sm"
            placeholder="Enter new password"
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#EE2E24] transition-colors"
          >
            {showPassword ? (
              <FaEyeSlash className="text-base" />
            ) : (
              <FaEye className="text-base" />
            )}
          </button>
        </div>
        {errors.newPassword && (
          <p className="text-[#EE2E24] text-xs mt-1 font-medium">
            {errors.newPassword}
          </p>
        )}
      </div>

      {/* Confirm Password Field */}
      <div>
        <label className="block text-xs font-bold text-[#2E2E2E] mb-1.5">
          Confirm Password
        </label>
        <div className="relative">
          <FaLock className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#EE2E24] text-xs" />
          <input
            type={showPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full pl-8 pr-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#EE2E24] focus:ring-2 focus:ring-[#FFC400]/20 outline-none transition-all text-sm"
            placeholder="Confirm new password"
            required
          />
        </div>
        {errors.confirmPassword && (
          <p className="text-[#EE2E24] text-xs mt-1 font-medium">
            {errors.confirmPassword}
          </p>
        )}
      </div>

      {/* Reset Button */}
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-gradient-to-r from-[#EE2E24] to-[#FFC400] text-white py-3 rounded-lg font-bold text-base shadow-md shadow-[#EE2E24]/30 hover:shadow-lg hover:shadow-[#FFC400]/40 transition-all hover:scale-[1.02] disabled:opacity-50"
      >
        {loading ? (
          <span className="flex items-center justify-center">
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Resetting...
          </span>
        ) : (
          "Reset Password"
        )}
      </button>
    </form>
  );

  // Device Warning Modal
  const renderDeviceWarningModal = () => {
    if (!deviceWarning) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div className="w-full max-w-md bg-white rounded-xl shadow-2xl p-6 animate-in fade-in zoom-in duration-200">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-[#FFC400]/20 rounded-full">
              <FaExclamationTriangle className="text-2xl text-[#EE2E24]" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#2E2E2E]">
                Device Already Logged In
              </h3>
              <p className="text-xs text-gray-600">Another session detected</p>
            </div>
          </div>

          {/* Content */}
          <div className="mb-6">
            <p className="text-sm text-gray-700 mb-4">
              This account is currently logged in on another device. You can
              either:
            </p>

            {/* Existing Sessions */}
            {deviceWarning.sessions && deviceWarning.sessions.length > 0 && (
              <div className="bg-[#F5F5F5] rounded-lg p-3 mb-4">
                <p className="text-xs font-bold text-[#2E2E2E] mb-2">
                  Active Sessions:
                </p>
                {deviceWarning.sessions.map((session, index) => (
                  <div
                    key={index}
                    className="text-xs text-gray-600 mb-1 flex items-center gap-2"
                  >
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    {session.deviceInfo || session.deviceId || "Unknown Device"}
                  </div>
                ))}
              </div>
            )}

            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-[#EE2E24] mt-0.5">•</span>
                <span>Cancel and use your other device</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#EE2E24] mt-0.5">•</span>
                <span>Continue here and logout from other devices</span>
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={() => {
                setDeviceWarning(null);
                setLoading(false);
              }}
              className="flex-1 px-4 py-2.5 border-2 border-gray-300 text-[#2E2E2E] rounded-lg font-bold text-sm hover:bg-gray-50 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleForceLogin}
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#EE2E24] to-[#FFC400] text-white rounded-lg font-bold text-sm shadow-md hover:shadow-lg transition-all disabled:opacity-50"
            >
              {loading ? "Logging in..." : "Continue Here"}
            </button>
          </div>

          {/* Info */}
          <p className="text-[10px] text-gray-500 text-center mt-4">
            For security, only one active session is allowed per account
          </p>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Device Warning Modal */}
      {renderDeviceWarningModal()}

      {/* Main Login Form */}
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F5F5F5] via-[#FFFFFF] to-[#FFC400]/10 p-2">
        <div className="w-full max-w-[340px] sm:max-w-[380px] bg-white/90 border-2 border-[#EE2E24]/20 backdrop-blur-sm rounded-xl shadow-xl shadow-[#EE2E24]/10 p-5 sm:p-6">
          {/* Elegant Header */}
          <div className="text-center mb-4">
            <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight bg-gradient-to-r from-[#EE2E24] to-[#FFC400] bg-clip-text text-transparent mb-1">
              {currentMode === "login" && "Sign in to Trivana"}
              {currentMode === "forgot" && "Forgot Password"}
              {currentMode === "otp-verify" && "Verify OTP"}
              {currentMode === "reset-password" && "Set New Password"}
            </h2>
            <p className="text-[#2E2E2E]/70 text-[11px] sm:text-xs font-semibold">
              {currentMode === "login" &&
                "Welcome back! Please login to your account."}
              {currentMode === "forgot" &&
                "We'll send you a code to reset your password."}
              {currentMode === "otp-verify" &&
                "Enter the code sent to your email or phone."}
              {currentMode === "reset-password" &&
                "Create a strong new password."}
            </p>
          </div>

          {/* Error Message */}
          {errors.general && (
            <div className="mb-3 p-2 bg-[#EE2E24]/10 border-l-4 border-[#EE2E24] rounded text-[11px] text-[#EE2E24] font-bold">
              {errors.general}
            </div>
          )}

          {/* Forms */}
          {currentMode === "login" && renderLoginForm()}
          {currentMode === "forgot" && renderForgotPasswordForm()}
          {currentMode === "otp-verify" && renderOTPForm()}
          {currentMode === "reset-password" && renderResetPasswordForm()}

          {/* Security Badge */}
          <div className="flex items-center justify-center gap-1.5 text-[10px] text-gray-400 mt-4 pt-3 border-t border-[#EE2E24]/10">
            <FaShieldAlt className="text-green-500 text-xs" />
            <span>Secured with encryption</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginForm;
